import ReactiveDialog from "../../common/ReactiveDialog";
import { Box, Typography, MenuItem } from "@mui/material";
import {
  useDeactivateFamily,
  useGetFamilyDetails,
} from "../../../services/advisor";
import Loading from "../../common/Loading";
import { Family } from "protogen/advisors_service_pb";
import { PlanUtilization } from "protogen/common_pb";
import { useContext, useEffect, useState } from "react";
import Select from "components/common/Select";
import {
  FAMILY_CHURN_REASONS,
  FAMILY_CHURN_REASONS_ADMIN_ONLY,
} from "components/common/MetadataSection";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";

type Props = {
  familyRef: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpdate: (family: Family) => void;
};

type DeactivationContentProps = {
  family: Family;
  planUtilization?: PlanUtilization;
  setReason: (reason: string) => void;
  isAdmin: boolean;
};

const DeactivationContent = ({
  planUtilization,
  setReason,
  isAdmin,
}: DeactivationContentProps) => {
  const availableReasons = isAdmin
    ? [...FAMILY_CHURN_REASONS, ...FAMILY_CHURN_REASONS_ADMIN_ONLY]
    : FAMILY_CHURN_REASONS;

  return (
    <Box display="flex" flexDirection="column" gap="12px">
      <Typography variant="bodyHeavy">
        Are you sure you want to deactivate this family's service?
        {planUtilization?.periodEndDate && (
          <Box component="span" display="block" mt={1}>
            The service will end on{" "}
            {new Date(planUtilization.periodEndDate).toLocaleDateString(
              "en-US",
              {
                day: "numeric",
                month: "long",
                year: "numeric",
                timeZone: "UTC",
              },
            )}
            .
          </Box>
        )}
        {!isAdmin && (
          <Typography variant="bodyHeavy" mt={1}>
            If you need to end the service earlier or a refund is required
            please reach out to{" "}
            <a href="mailto:support@findfaye.com" style={{ color: "#198282" }}>
              support
            </a>
            .
          </Typography>
        )}
      </Typography>
      <Select
        variant="outlined"
        placeholder="Reason for deactivation"
        displayEmpty
        defaultValue={""}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        sx={{
          "& .MuiInputBase-input": {
            marginTop: "0px !important",
          },
          width: "100%",
          height: "60px",
        }}
      >
        <MenuItem key={""} value={""} disabled>
          Churn reason
        </MenuItem>
        {availableReasons.sort().map((reason) => (
          <MenuItem key={reason} value={reason}>
            {reason}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default ({ familyRef, open, setOpen, onUpdate }: Props) => {
  const {
    data: familyData,
    request: requestFamily,
    loading: loadingFamily,
  } = useGetFamilyDetails();
  const { request, loading } = useDeactivateFamily();
  const [reason, setReason] = useState("");
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);

  useEffect(() => {
    if (open) {
      requestFamily({ familyRef });
      setReason(""); // Reset reason when dialog opens
    }
  }, [open, familyRef]);

  const onDeactivation = async () => {
    if (loading || !familyData || !reason) return;
    const resp = await request({
      familyRef: familyData?.family?.ref || "",
      reason,
    });
    if (resp?.family) {
      onUpdate(resp.family);
    }
  };

  return (
    <ReactiveDialog
      open={open}
      onClose={() => setOpen(false)}
      title="Deactivate family"
      primaryActionName="Deactivate"
      primaryActionEnabled={!loading && !!familyData?.family && !!reason}
      primaryAction={onDeactivation}
      primaryActionColor="error"
      fullWidthSize="sm"
    >
      {loadingFamily ? (
        <Box display="flex" justifyContent="center" p={3}>
          <Loading />
        </Box>
      ) : familyData?.family ? (
        <DeactivationContent
          family={familyData.family}
          planUtilization={familyData.planUtilization}
          setReason={setReason}
          isAdmin={isAdmin}
        />
      ) : null}
    </ReactiveDialog>
  );
};
